import React from "react";
import DOMPurify from "dompurify";
import { Box, Typography } from "@mui/material";
import {
  generateWebPageStructuredData,
  generateBreadcrumbStructuredData,
  generateOrganizationStructuredData,
} from "../utils/structuredData";
import { SITE_NAME, REACT_APP_SITE_URL } from "../config/seo";
import { companyInfoData } from "../services/apiService";
import { SEO } from "../components/SEO";
import {
  AnimatedInView,
  Section,
  GradientHero,
  GradientText,
  GlassCard,
  BrandStrip,
  StatCounter,
  CTABand,
} from "../components/ui";

const PAGE_DESCRIPTION =
  "Founded in 2011 in Delhi, R S Enterprises is a trusted wholesale trader and service provider of ECR machines, POS systems and billing solutions across India.";

const About: React.FC = () => {
  // Static data — synchronous for prerender/hydration parity
  const companyInfo = companyInfoData;
  const pageUrl = `${REACT_APP_SITE_URL}/about`;

  return (
    <>
      <SEO
        title="About Us"
        description={PAGE_DESCRIPTION}
        jsonLd={[
          generateWebPageStructuredData({
            title: `About Us | ${SITE_NAME}`,
            description: PAGE_DESCRIPTION,
            url: pageUrl,
          }),
          generateOrganizationStructuredData(),
          generateBreadcrumbStructuredData([
            { name: "Home", item: REACT_APP_SITE_URL },
            { name: "About", item: pageUrl },
          ]),
        ]}
      />

      <GradientHero
        compact
        eyebrow="Our Story"
        title={
          <>
            Built on trust <GradientText>since 2011</GradientText>
          </>
        }
        subtitle={companyInfo?.aboutUsTitle || "Learn more about our company and what makes us different"}
      />

      {/* Company story */}
      <Section bg="paper" id="about-story">
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: { xs: "1fr", md: "8fr 4fr" },
            gap: { xs: 5, md: 8 },
            alignItems: "start",
          }}
        >
          <AnimatedInView>
            {companyInfo && (
              <Box
                sx={{
                  fontSize: "1.1rem",
                  lineHeight: 1.8,
                  color: "text.secondary",
                  "& p": {
                    margin: "0 0 20px 0",
                    "&:last-child": { marginBottom: 0 },
                  },
                  "& ul": {
                    margin: "20px 0",
                    paddingLeft: "28px",
                    "& li": {
                      marginBottom: "10px",
                      position: "relative",
                      "&:before": {
                        content: '"\\2022"',
                        color: "secondary.main",
                        fontWeight: "bold",
                        display: "inline-block",
                        width: "1em",
                        marginLeft: "-1em",
                        fontSize: "1.2em",
                        lineHeight: 1,
                      },
                      "&:last-child": { marginBottom: 0 },
                    },
                  },
                  "& strong": {
                    color: "text.primary",
                    fontWeight: 600,
                  },
                }}
                dangerouslySetInnerHTML={{
                  __html: DOMPurify.sanitize(companyInfo.aboutUs),
                }}
              />
            )}
          </AnimatedInView>

          {/* Stats sidebar */}
          {companyInfo && (
            <AnimatedInView delay={0.15}>
              <Box sx={{ display: "grid", gap: 2.5 }}>
                {companyInfo.stats.map((stat) => (
                  <GlassCard
                    key={stat.label}
                    tone="light"
                    sx={{
                      bgcolor: "primary.dark",
                      border: "none",
                      color: "common.white",
                      py: 3,
                    }}
                  >
                    <StatCounter value={stat.value} suffix={stat.suffix} label={stat.label} />
                  </GlassCard>
                ))}
              </Box>
            </AnimatedInView>
          )}
        </Box>
      </Section>

      {/* Brands */}
      <Section py={{ xs: 5, md: 6 }}>
        <AnimatedInView variant="fade">
          <BrandStrip />
        </AnimatedInView>
      </Section>

      {/* Values */}
      {companyInfo && (
        <Section bg="paper" id="our-values">
          <AnimatedInView>
            <Typography variant="overline" component="p" sx={{ color: "secondary.dark" }}>
              Our Promise
            </Typography>
            <Typography variant="h2" component="h2" sx={{ mb: 4 }}>
              Why businesses stay with us
            </Typography>
          </AnimatedInView>
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: { xs: "1fr", sm: "repeat(2, 1fr)", md: "repeat(3, 1fr)" },
              gap: 3,
            }}
          >
            {companyInfo.whyChooseUs.map((item, index) => (
              <AnimatedInView key={item} delay={index * 0.05}>
                <GlassCard tone="light" sx={{ height: "100%" }}>
                  <Typography
                    variant="h2"
                    component="p"
                    aria-hidden
                    sx={{ color: "secondary.light", fontWeight: 800, mb: 1, opacity: 0.6 }}
                  >
                    {String(index + 1).padStart(2, "0")}
                  </Typography>
                  <Typography variant="body1" sx={{ fontWeight: 500 }}>
                    {item}
                  </Typography>
                </GlassCard>
              </AnimatedInView>
            ))}
          </Box>
        </Section>
      )}

      <CTABand
        title="Let's put the right machine on your counter"
        subtitle="Visit our Laxmi Nagar office, call us, or drop a WhatsApp message — we respond quickly."
      />
    </>
  );
};

export default About;
